/**
 * Runtime database binding — Sprint 17 (Connection Runtime Engine).
 *
 * Single source of truth for the active customer-project Firebase runtime.
 * Data-access services import `runtimeDb` (aliased to `db`) instead of the
 * default Studio app. Because `runtimeDb` is an `export let` live binding, any
 * `collection(db, ...)` / `doc(db, ...)` call automatically targets whatever
 * customer project is currently active.
 *
 * Studio METADATA services keep importing `db` from `src/firebase` directly.
 * Only CUSTOMER-DATA services import from here.
 */
import { db as studioDb } from "../../../firebase";

// Live binding. Reassigned by setRuntimeContext / resetRuntimeDb.
// eslint-disable-next-line import/no-mutable-exports
export let runtimeDb = studioDb;
// eslint-disable-next-line import/no-mutable-exports
export let runtimeApp = null;
// eslint-disable-next-line import/no-mutable-exports
export let runtimeAuth = null;

let _activeConnectionId = null;
let _activeProjectId = null;
let _lastRuntimeRefresh = null;
let _runtimeVersion = 0;

const listeners = new Set();

function notifyListeners() {
  listeners.forEach((fn) => {
    try { fn(getRuntimeContextSnapshot()); } catch { /* ignore */ }
  });
}

/** Immutable Studio/default Firestore (studio_* metadata lives here). */
export function getStudioDb() {
  return studioDb;
}

/**
 * Activate the shared runtime binding for a customer connection.
 * @param {{ firestore, app?, auth?, connectionId, projectId? }} ctx
 */
export function setRuntimeContext({ firestore, app = null, auth = null, connectionId, projectId = null }) {
  if (!firestore || !connectionId) {
    resetRuntimeDb();
    return;
  }
  runtimeDb = firestore;
  runtimeApp = app || firestore?.app || null;
  runtimeAuth = auth;
  _activeConnectionId = connectionId;
  _activeProjectId = projectId || runtimeApp?.options?.projectId || null;
  _lastRuntimeRefresh = new Date().toISOString();
  _runtimeVersion += 1;
  notifyListeners();
}

/** @deprecated Use setRuntimeContext — kept for internal callers. */
export function setRuntimeDb(firestore, connectionId = null, projectId = null) {
  if (!firestore || !connectionId) {
    resetRuntimeDb();
    return;
  }
  setRuntimeContext({
    firestore,
    app: firestore?.app || null,
    auth: null,
    connectionId,
    projectId,
  });
}

/** Reset the runtime binding back to the Studio/default Firestore. */
export function resetRuntimeDb() {
  runtimeDb = studioDb;
  runtimeApp = null;
  runtimeAuth = null;
  _activeConnectionId = null;
  _activeProjectId = null;
  _lastRuntimeRefresh = null;
  _runtimeVersion += 1;
  notifyListeners();
}

/** Current runtime Firestore (customer project when active, else Studio). */
export function getRuntimeDb() {
  return runtimeDb;
}

/** Connection id backing the current runtime binding (null when default). */
export function getActiveRuntimeConnectionId() {
  return _activeConnectionId;
}

/** Firebase project id for the active customer runtime. */
export function getActiveRuntimeProjectId() {
  return _activeProjectId;
}

/** ISO timestamp of the last runtime activation or reset. */
export function getLastRuntimeRefresh() {
  return _lastRuntimeRefresh;
}

/** Monotonic counter — increments on every runtime switch for effect deps. */
export function getRuntimeVersion() {
  return _runtimeVersion;
}

/** True when a customer-project runtime is active (not the Studio default). */
export function hasActiveRuntime() {
  return Boolean(_activeConnectionId) && runtimeDb !== studioDb;
}

/** Plain snapshot for Runtime Inspector and non-React subscribers. */
export function getRuntimeContextSnapshot() {
  return {
    runtimeApp,
    runtimeDb,
    runtimeAuth,
    runtimeConnectionId: _activeConnectionId,
    runtimeProjectId: _activeProjectId,
    lastRuntimeRefresh: _lastRuntimeRefresh,
    runtimeVersion: _runtimeVersion,
    isActive: hasActiveRuntime(),
    firebaseAppName: runtimeApp?.name || null,
    firestoreAppName: runtimeDb?.app?.name || null,
    databaseId: runtimeDb?._databaseId?.database || "(default)",
  };
}

/** Subscribe to runtime context changes. Returns unsubscribe fn. */
export function subscribeRuntimeContext(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Verify runtime binding matches the active workspace connection.
 * @returns {{ ok: boolean, reason?: string }}
 */
export function checkRuntimeWorkspaceAlignment({
  workspaceConnectionId,
  runtimeConnectionId,
  runtimeProjectId,
  expectedFirebaseProjectId,
}) {
  if (!workspaceConnectionId) {
    return { ok: false, reason: "no_connection" };
  }
  if (!hasActiveRuntime()) {
    return { ok: false, reason: "no_runtime" };
  }
  if (runtimeConnectionId && runtimeConnectionId !== workspaceConnectionId) {
    return { ok: false, reason: "connection_mismatch" };
  }
  if (
    expectedFirebaseProjectId &&
    runtimeProjectId &&
    runtimeProjectId !== expectedFirebaseProjectId
  ) {
    return { ok: false, reason: "project_mismatch" };
  }
  return { ok: true };
}

/**
 * Assert that a customer runtime is active before customer-data operations.
 * Throws if the binding still points at the Studio default or mismatches.
 */
export function requireCustomerRuntime(expectedConnectionId = null) {
  if (!hasActiveRuntime()) {
    throw new Error("No active workspace connection.");
  }
  if (expectedConnectionId && getActiveRuntimeConnectionId() !== expectedConnectionId) {
    throw new Error(
      `Runtime mismatch: active connection is "${getActiveRuntimeConnectionId()}", ` +
      `but "${expectedConnectionId}" was requested.`
    );
  }
  return runtimeDb;
}
